import { Eye, EyeOff } from 'lucide-react';
import { RatingPicker } from '@/components/forms/RatingPicker';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

export interface PrizeModeValue {
  prizeMode: 'weight' | 'cooldown';
  rating: number | null;
  cooldownMinutes: number | null;
  hiddenFromGames: boolean;
}

/**
 * Los mandos de un regalo en una estación: peso o cadencia, y el «ojo».
 *
 * Es el MISMO bloque en el planner (quien organiza) y en `/operator/:id`
 * (quien atiende el stand con el código): la regla es una y se aprende una
 * vez. Peso y cadencia son excluyentes a propósito —un radio, no dos
 * interruptores—: quien lleva la estación elige cómo frena un regalo, y
 * combinar los dos daba un mando que nadie sabía leer.
 *
 * No guarda nada: devuelve el parche por `onChange` y quien lo monta decide
 * contra qué endpoint va (el PUT completo del admin o el PATCH recortado del
 * operador).
 */
export function AllocationPrizeModeFields({
  value,
  stockRating,
  disabled,
  compact,
  onChange,
}: {
  value: PrizeModeValue;
  /** El rating del regalo, para enseñarlo cuando la asignación no lo pisa. */
  stockRating?: number | null;
  disabled?: boolean;
  /** En el operador: sin rótulos largos, todo en una columna. */
  compact?: boolean;
  onChange: (patch: Partial<PrizeModeValue>) => void;
}) {
  const t = useT();
  const ratingActual = value.rating ?? stockRating ?? 3;
  const modo = value.prizeMode ?? 'weight';

  return (
    <div className={cn('flex flex-col gap-2', compact ? 'text-sm' : 'text-[11px]')}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-[var(--color-xk-text-muted)]">
          {t('planner.alloc.mode.label')}
        </span>
        <div
          role="radiogroup"
          aria-label={t('planner.alloc.mode.label')}
          className="inline-flex overflow-hidden rounded-full border border-[var(--color-border-default)]"
        >
          {(['weight', 'cooldown'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={modo === m}
              disabled={disabled}
              onClick={() => {
                if (m === modo) return;
                // Al pasar a cadencia sin minutos, un defecto razonable: el
                // servidor rechaza el modo sin minutos, y pedirlos antes de
                // dejar cambiar el radio es un formulario que no se entiende.
                onChange(
                  m === 'cooldown'
                    ? { prizeMode: m, cooldownMinutes: value.cooldownMinutes ?? 10 }
                    : { prizeMode: m },
                );
              }}
              className={cn(
                'px-3 py-1 font-semibold transition-colors disabled:opacity-50',
                compact ? 'min-h-[40px]' : 'min-h-[26px]',
                modo === m
                  ? 'bg-[var(--color-accent)] text-[var(--color-fg-on-accent)]'
                  : 'bg-[var(--color-bg-surface)] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]',
              )}
            >
              {m === 'weight' ? t('planner.alloc.mode.weight') : t('planner.alloc.mode.cooldown')}
            </button>
          ))}
        </div>
      </div>

      {modo === 'weight' ? (
        <div className="flex flex-wrap items-center gap-2">
          <RatingPicker
            value={ratingActual}
            size={compact ? 'md' : 'sm'}
            readOnly={disabled}
            onChange={(n) => onChange({ rating: n })}
          />
          <span className="text-[var(--color-xk-text-muted)]">{t('planner.alloc.mode.weightHint')}</span>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2">
            <span className="text-[var(--color-xk-text-muted)]">{t('planner.alloc.mode.cooldownMinutes')}</span>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              max={1440}
              disabled={disabled}
              value={value.cooldownMinutes ?? ''}
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                if (Number.isFinite(n) && n >= 1 && n <= 1440) onChange({ cooldownMinutes: n });
              }}
              className={cn('w-20 text-center tabular-nums', compact ? 'h-10' : 'h-7 text-[11px]')}
            />
          </label>
          <span className="text-[var(--color-xk-text-muted)]">{t('planner.alloc.mode.cooldownHint')}</span>
        </div>
      )}

      <div>
        <Button
          variant={value.hiddenFromGames ? 'primary' : 'secondary'}
          size={compact ? 'md' : 'sm'}
          disabled={disabled}
          leftIcon={value.hiddenFromGames ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          onClick={() => onChange({ hiddenFromGames: !value.hiddenFromGames })}
        >
          {value.hiddenFromGames ? t('planner.alloc.showInGames') : t('planner.alloc.hideFromGames')}
        </Button>
      </div>
    </div>
  );
}
